/**
 * Pure helpers for white-label theming of public pages (`/check?org=…`).
 * Branding colors come from org-controlled data, so every value is validated
 * before it is written into CSS custom properties.
 */

/** Returns a `#rrggbb` string, or null when the value is not a safe hex color. */
export function normalizeHexColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
  if (short) {
    const digits = short[1];
    return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`.toLowerCase();
  }
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed.toLowerCase() : null;
}

/** Darkens a `#rrggbb` color; used to derive the hover shade from the primary color. */
export function darkenHexColor(hex: string, amount = 0.12): string {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return hex;
  const channels = [1, 3, 5].map((offset) => {
    const channel = parseInt(normalized.slice(offset, offset + 2), 16);
    return Math.max(0, Math.round(channel * (1 - amount)));
  });
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** `#rrggbb` → `"r, g, b"` for the `--primary-color-rgb` token. */
export function hexToRgbChannels(hex: string): string | null {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  const channels = [1, 3, 5].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16));
  return channels.join(', ');
}
